import { BioSampleUpdateError, CorrelationIdAndErrorV1 } from '@csp/csp-common-hbs-model';
import { Maybe } from '@csp/csp-common-model';
import {
  BioSampleBatchCreateResultsV1,
  BioSampleBatchUpsertCustomStatusesResultsV1,
  BioSampleId,
  ErrorV1,
} from '@csp/dmdp-api-hbs-dto';
import { BioSampleErrorCodeMapper } from './BioSampleErrorCodeMapper';

type BatchCreateResult = {
  error?: ErrorV1;
  bioSampleId?: BioSampleId;
  correlationId: string;
};

const fromErrorV1 = <T>(errorV1: Maybe<ErrorV1>, sampleIn: Maybe<T>): BioSampleUpdateError<T> => ({
  sampleIn,
  errorCode: BioSampleErrorCodeMapper.fromDmdpErrorCode(errorV1?.errorCode),
});

const fromResult = <T>(
  result: CorrelationIdAndErrorV1[],
  sampleIns: Array<{ correlationId: string; updateIn: T }>,
): BioSampleUpdateError<T>[] =>
  result
    .filter(idOrError => !!idOrError.error)
    .map(idOrError =>
      fromErrorV1(
        idOrError.error,
        sampleIns.find(sampleIn => sampleIn.correlationId === idOrError.correlationId)?.updateIn,
      ),
    );

const fromBioSampleBatchCreateResultsV1 = <T>(
  v1: BioSampleBatchCreateResultsV1,
  sampleIns: Array<{ correlationId: string; updateIn: T }>,
): BioSampleUpdateError<T>[] =>
  fromResult(
    v1.bioSampleBatchCreateResults.map(result => {
      const { correlationId, error } = result as BatchCreateResult;
      return {
        correlationId,
        error,
      };
    }),
    sampleIns,
  );

const fromBioSampleBatchUpsertCustomStatusesResultsV1 = <T>(
  v1: BioSampleBatchUpsertCustomStatusesResultsV1,
  sampleIns: Array<{ correlationId: string; updateIn: T }>,
): BioSampleUpdateError<T>[] =>
  fromResult(
    v1.bioSampleBatchUpsertCustomStatusesResults.map(result => ({
      correlationId: result.bioSampleId,
      error: result.error,
    })),
    sampleIns,
  );

export const BioSampleErrorMapper = {
  fromErrorV1,
  fromResult,
  fromBioSampleBatchCreateResultsV1,
  fromBioSampleBatchUpsertCustomStatusesResultsV1,
};
